import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SelectChipStates, useTheme } from '@Sage/hoi-poi-ui';
import { BaseReactCell } from '@Sage/ssm-web5';
import { getLiteral } from 'utils/getLiteral';
import { errorToast, successToast } from 'utils/toast';
import { buildMarketingStatusChipOptions, fetchMarketingStatusList } from './marketingStatusList';
import { NOOP_SET_MARKETING_CONSENT } from './column';
import MarketingStatusReadOnlyChip from './MarketingStatusReadOnlyChip';
import './styles.scss';

function normalizeStatus(value, allowedIds, options, data) {
    const idFromRow = data?.marketingStatusId ?? data?.MarketingStatusId;
    if (idFromRow != null && idFromRow !== '') {
        const fromRow = Number(idFromRow);
        if (!Number.isNaN(fromRow)) {
            if (!allowedIds?.length) return fromRow;
            return allowedIds.includes(fromRow) ? fromRow : allowedIds[0];
        }
    }
    if (!allowedIds?.length) {
        const parsed = Number(value);
        return Number.isNaN(parsed) ? 0 : parsed;
    }
    if (value === null || value === undefined || value === '') {
        return allowedIds[0];
    }
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
        if (typeof value === 'string' && options?.length) {
            const t = value.trim();
            const byLabel = options.find((o) => String(o.label).trim() === t);
            if (byLabel != null && allowedIds.includes(byLabel.value)) {
                return byLabel.value;
            }
        }
        return allowedIds[0];
    }
    return allowedIds.includes(parsed) ? parsed : allowedIds[0];
}

const MarketingStatusCell = memo(
    ({
        data,
        value,
        onChange,
        setMarketingConsent = NOOP_SET_MARKETING_CONSENT,
        isReadOnly = false,
        ...props
    }) => {
        const dispatch = useDispatch();
        const theme = useTheme();
        const [listRows, setListRows] = useState([]);

        useEffect(() => {
            fetchMarketingStatusList(dispatch)
                .then((rows) => setListRows(Array.isArray(rows) ? rows : []))
                .catch(() => setListRows([]));
        }, [dispatch]);

        const options = useMemo(
            () => buildMarketingStatusChipOptions(listRows, theme),
            [listRows, theme],
        );

        const allowedIds = useMemo(() => options.map((o) => o.value), [options]);

        const [localStatus, setLocalStatus] = useState(() =>
            normalizeStatus(value, allowedIds, options, data),
        );

        useEffect(() => {
            setLocalStatus(normalizeStatus(value, allowedIds, options, data));
        }, [value, allowedIds, options, data]);

        const handleChange = useCallback(
            async (opt) => {
                const entityId = data?.Id != null ? Number(data.Id) : NaN;
                if (Number.isNaN(entityId)) {
                    return;
                }
                const previous = normalizeStatus(value, allowedIds, options, data);
                setLocalStatus(opt.value);
                try {
                    await setMarketingConsent({
                        id: entityId,
                        marketingStatusId: opt.value,
                        row: data,
                    });
                    onChange?.(data, opt);
                    if (setMarketingConsent !== NOOP_SET_MARKETING_CONSENT) {
                        successToast({
                            title: getLiteral(
                                'title_toast_marketing_campaigns_marketing_status_update_success',
                            ),
                            text: getLiteral(
                                'label_toast_marketing_campaigns_marketing_status_update_success_message',
                            ),
                        });
                    }
                } catch {
                    setLocalStatus(previous);
                    if (setMarketingConsent !== NOOP_SET_MARKETING_CONSENT) {
                        errorToast({
                            title: getLiteral(
                                'title_toast_marketing_campaigns_marketing_status_update_error',
                            ),
                            text: getLiteral(
                                'label_toast_marketing_campaigns_marketing_status_update_error_message',
                            ),
                        });
                    }
                }
            },
            [allowedIds, data, onChange, options, setMarketingConsent, value],
        );

        const selectedOption = useMemo(
            () => options.find((o) => o.value === localStatus),
            [options, localStatus],
        );

        return (
            <BaseReactCell data={data} {...props}>
                <div className="fm-marketing-status-cell">
                    {options.length > 0 ? (
                        isReadOnly ? (
                            <MarketingStatusReadOnlyChip option={selectedOption} />
                        ) : (
                            <SelectChipStates
                                options={options}
                                value={localStatus}
                                onChange={handleChange}
                                title={getLiteral('label_marketing_campaigns_change_status')}
                            />
                        )
                    ) : null}
                </div>
            </BaseReactCell>
        );
    },
);

MarketingStatusCell.displayName = 'MarketingStatusCell';

export default MarketingStatusCell;
